import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { useBoolean } from '@sravni/react-utils';

import { DEFAULT_FINGER_PRINT_VALUE } from '../../../constants/fingerPrint';
import { IVotesByReviewId } from '../../../interfaces/reviews';
import { getVotes } from '../../../services/reviews';
import { useFingerprint } from '../../../store/hooks/users';
import { IReviewsListState, setReviewsList } from '../../../store/reducers/reviews';

type TUseFetchLikesAndSetForInitialReviews = {
  isFetchingReviewLikes: boolean;
};

export const useFetchLikesAndSetForInitialReviews = (
  reviews: IReviewsListState,
): TUseFetchLikesAndSetForInitialReviews => {
  const fp = useFingerprint();
  const dispatch = useDispatch();
  const [isFetchingReviewLikes, setIsFetchingReviewLikes] = useBoolean(true);

  const getReviewsItems = useCallback((reviewsState: IReviewsListState, votes: IVotesByReviewId) => {
    return reviewsState.items.map((review) => {
      const voteForReview = votes?.[review.id] || {};

      return {
        ...review,
        vote: voteForReview?.userVote ?? null,
        votes: voteForReview?.votes ?? { positive: 0, negative: 0 },
      };
    });
  }, []);

  const fetchLikesAndSetForInitialReviews = useCallback(
    async (reviewsState: IReviewsListState) => {
      const ids = reviewsState?.items?.map((item) => item?.id);

      try {
        if (!ids?.length) return;

        const { data: votes } = await getVotes({
          ReviewIds: ids,
          Fingerprint: fp,
        });

        dispatch(
          setReviewsList({
            ...reviewsState,
            items: getReviewsItems(reviewsState, votes),
          }),
        );
      } catch (err) {
        console.error(err);
      } finally {
        setIsFetchingReviewLikes.off();
      }
    },
    [dispatch, fp, getReviewsItems, setIsFetchingReviewLikes],
  );

  useEffect(() => {
    if (fp !== DEFAULT_FINGER_PRINT_VALUE) {
      fetchLikesAndSetForInitialReviews(reviews);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fp]);

  return { isFetchingReviewLikes };
};
